import { handleServerSyncRequest } from "@/lib/interactionLog/sync";
import { isRemote } from "@/lib/remote";
import { useCallback, useEffect, useRef, useState } from "react";

// A tablet that suspends leaves a socket the browser still calls OPEN. Only a
// round trip proves the pipe is alive, so we ping and give the server a window.
const PING_INTERVAL_MS = 25000;
const PONG_TIMEOUT_MS = 20000;

// Backoff for a server that is down. Jitter keeps twelve captains' tablets from
// reconnecting in lockstep and hammering the API the moment it comes back.
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 30000;

const backoffFor = attempt =>
  Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt) * (0.5 + Math.random() * 0.5);

const STATUS = {
  connecting: { mode: "connecting", label: "Connecting terminals" },
  live: { mode: "network", label: "Terminals live" },
  offline: { mode: "offline", label: "Sync reconnecting" },
  disabled: { mode: "local", label: "Local database ready" },
};

const socketUrl = (token, role, since, epoch, remote = false) => {
  let base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
  if (!base && typeof window !== "undefined" && window.location?.host) {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    base = `${proto}//${window.location.host}`;
  }
  const params = new URLSearchParams();
  // Documented LAN-only exposure: the JWT rides in the query string because the
  // browser WebSocket API cannot set headers. See CLAUDE.md "API Conventions".
  // Through RestoYaar Connect it may not (bible §64 rule 8): the token goes in the
  // first message instead, and the anonymous kitchen carve-out does not exist.
  if (remote) {
    // nothing: authenticated by the first message
  } else if (token) params.set("token", token);
  else params.set("role", role || "kds");
  if (since !== null && since !== undefined) params.set("since", String(since));
  // Event ids are per-process and restart at 0. Without the epoch the server cannot
  // tell a stale cursor from a current one and would answer "nothing missed".
  if (epoch) params.set("epoch", epoch);
  return `${base}/api/ws?${params.toString()}`;
};

/**
 * Live cross-terminal updates.
 *
 * The server stamps every broadcast with a monotonic event_id and keeps a bounded
 * replay buffer, so a reconnect after a short drop delivers exactly the missed
 * events. When the gap is too old the server says `resync_required` and this hook
 * reports it rather than letting the screen quietly drift — a KOT the kitchen never
 * saw is worse than a spinner.
 *
 * @param onEvent called with {type, orderId} for each server event, and with
 *   {type: "resync"} when the client must re-read from REST.
 */
export function useRealtimeSync(enabled, token, terminalId, role, onEvent) {
  const [status, setStatus] = useState(STATUS.disabled);
  const handlerRef = useRef(onEvent);
  const lastEventId = useRef(null);
  const serverEpoch = useRef(null);

  // Keep the latest callback without making it a dependency: a new function
  // identity each render would tear down and rebuild the socket every render.
  handlerRef.current = onEvent;

  const emit = useCallback(event => {
    if (typeof handlerRef.current === "function") handlerRef.current(event);
  }, []);

  useEffect(() => {
    const remote = isRemote();
    if (!enabled || (!token && (role !== "kds" || remote))) {
      setStatus(STATUS.disabled);
      return undefined;
    }

    let socket = null;
    let active = true;
    let attempt = 0;
    let reconnectTimer = null;
    let pingTimer = null;
    let pongTimer = null;

    const clearTimers = () => {
      window.clearTimeout(reconnectTimer);
      window.clearInterval(pingTimer);
      window.clearTimeout(pongTimer);
      pongTimer = null;
    };

    const scheduleReconnect = () => {
      if (!active) return;
      setStatus(STATUS.offline);
      reconnectTimer = window.setTimeout(connect, backoffFor(attempt));
      attempt += 1;
    };

    const startHeartbeat = () => {
      pingTimer = window.setInterval(() => {
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({ type: "ping", terminalId }));
        // A pong that never arrives means the socket is dead in a way onclose
        // will not tell us about. Close it ourselves and let backoff take over.
        if (pongTimer === null) {
          pongTimer = window.setTimeout(() => socket?.close(), PONG_TIMEOUT_MS);
        }
      }, PING_INTERVAL_MS);
    };

    function connect() {
      if (!active) return;
      setStatus(STATUS.connecting);
      try {
        socket = new WebSocket(
          socketUrl(token, role, lastEventId.current, serverEpoch.current, remote)
        );
      } catch {
        scheduleReconnect();
        return;
      }

      socket.onopen = () => {
        if (remote) socket.send(JSON.stringify({ type: "auth", token }));
        attempt = 0;
        setStatus(STATUS.live);
        startHeartbeat();
      };

      socket.onmessage = event => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (typeof message.event_id === "number") lastEventId.current = message.event_id;

        switch (message.type) {
          case "hello": {
            // A different epoch means the API restarted: our cursor belongs to a
            // counter that no longer exists, so drop it before it is sent again.
            const restarted = serverEpoch.current && serverEpoch.current !== message.epoch;
            if (restarted) lastEventId.current = null;
            serverEpoch.current = message.epoch ?? null;
            // replayed === false means the buffer could not cover our gap. Anything
            // that happened while we were away is lost, so re-read from REST.
            if (message.replayed === false || restarted) emit({ type: "resync" });
            break;
          }
          case "resync_required":
            emit({ type: "resync" });
            break;
          case "pong":
            window.clearTimeout(pongTimer);
            pongTimer = null;
            break;
          case "order_update":
            emit({ type: "order_update", orderId: Number(message.order_id ?? message.id) });
            break;
          case "printer_event":
            // `alert` rides only on a ticket that will not print now (held,
            // sent to the backup, given up on); see main.py _printer_alert.
            emit({
              type: "printer_event",
              printerEventId: message.id,
              ...(message.alert ? {
                alert: true,
                eventType: message.event_type,
                printerId: message.printer_id,
                printerName: message.printer_name,
                text: message.text,
              } : {}),
            });
            break;

          // settings_updated fell through this switch and was dropped. The whole
          // chain behind it already worked -- PUT /api/settings ->
          // settings_service.broadcast_settings_invalidation -> pg_notify ->
          // main.py::_on_settings_update -> manager.broadcast to every role -- so
          // the message arrived at the browser and nothing listened. An owner
          // changing a setting on one terminal saw no other terminal move until
          // it was reloaded.
          case "settings_updated":
            emit({ type: "settings_updated", keys: message.keys || [] });
            break;
          // Admin → Device Logs → Force sync. Handled here, not emitted: uploading the
          // log needs no screen to be mounted.
          case "logs_sync_requested":
            handleServerSyncRequest(message);
            break;
          default:
            break;
        }
      };

      socket.onclose = () => {
        window.clearInterval(pingTimer);
        window.clearTimeout(pongTimer);
        pongTimer = null;
        scheduleReconnect();
      };

      socket.onerror = () => socket?.close();
    }

    connect();

    return () => {
      active = false;
      clearTimers();
      // Drop the handler first: a close during teardown must not schedule a
      // reconnect against an unmounted component.
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    };
  }, [emit, enabled, role, terminalId, token]);

  return status;
}
